'use client';
import { useMemo } from 'react';
import type { AttributeComponent, AttsAssignment, AttsAttribute } from '@/endpoints/channel/getAttributeSpec';
import { getAssignmentCommonalityConfig, type CommonalityConfigInput } from '@/utils/postingTemplate/commonality';
import type { CommonalityByAssignment, PostingTemplateMapping, RenderResult } from '@/types/postingTemplate';

type MaybeArray<T> = T | T[];

// Inputs are derived from the shared domain types (Partial/Pick) instead of
// re-declaring the fields locally, so this hook cannot drift from the real
// shapes. The extra assignment_id / is_required / attribute_name fields predate
// the atts spec types and are kept for callers that still provide them.
type Assignment = Partial<Pick<AttsAssignment, 'is_mappable' | 'min_occurs' | 'max_occurs' | 'ignore_validation'>> & {
  assignment_id?: number;
  is_required?: number;
  attribute_name?: string;
  atts_attribute?: Partial<Pick<AttsAttribute, 'frontend_name' | 'commonality_key'>>;
};

type RenderedEntry = RenderResult;

type RenderedByAssignment = Record<string | number, MaybeArray<RenderedEntry>> | null;

type CategoryFilterAttribute = Pick<AttributeComponent, 'assignment_id'>;
type CategoryFilterContainer = {
  assignment_id?: string | number;
  containerComponents?: CategoryFilterContainer[];
  attributeComponents?: CategoryFilterAttribute[];
} | null;

type UseTemplateStatisticsProps = {
  assignmentById?: Record<string | number, Assignment>;
  postingTemplateMappings?: PostingTemplateMapping[];
  renderedByAssignment?: RenderedByAssignment;
  renderErrorsByAssignment?: Record<string | number, MaybeArray<unknown>> | null;
  fieldNames?: string[];
  allCommonality?: Record<string, CommonalityConfigInput>;
  commonalityByAssignment?: CommonalityByAssignment;
  modifiedMappings?: Set<string>;
  categoryFilter?: CategoryFilterContainer;
};

export type TemplateStatistics = {
  totalMappableAttributes: number;
  totalMappings: number;
  mappedAttributes: number;
  unmappedAttributes: number;
  totalRequired: number;
  mappedRequired: number;
  unmappedRequired: number;
  mappingErrors: number;
  modifiedAttributes: number;
  usesCommonality: number;
  availableCommonality: number;
  unmappedWithCommonality: number;
  redundantMappings: number;
  totalRendered: number;
  renderSuccess: number;
  renderEmpty: number;
  renderErrors: number;
  renderEmptyRequired: number;
  hasRenderData: boolean;
  mappedPercentage: number;
  requiredMappedPercentage: number;
};

const createEmptyStatistics = (): TemplateStatistics => ({
  totalMappableAttributes: 0,
  totalMappings: 0,
  mappedAttributes: 0,
  unmappedAttributes: 0,
  totalRequired: 0,
  mappedRequired: 0,
  unmappedRequired: 0,
  mappingErrors: 0,
  modifiedAttributes: 0,
  usesCommonality: 0,
  availableCommonality: 0,
  unmappedWithCommonality: 0,
  redundantMappings: 0,
  totalRendered: 0,
  renderSuccess: 0,
  renderEmpty: 0,
  renderErrors: 0,
  renderEmptyRequired: 0,
  hasRenderData: false,
  mappedPercentage: 0,
  requiredMappedPercentage: 0,
});

const isMappingValid = (mapping: PostingTemplateMapping | null | undefined): boolean => {
  if (!mapping) return false;
  if (mapping.value_type === 'commonality') return true;
  return mapping.attribute_value !== null && mapping.attribute_value !== undefined && mapping.attribute_value !== '';
};

const normalizeRenderedEntries = (entry: MaybeArray<RenderedEntry> | undefined): RenderedEntry[] => {
  if (!entry) return [];
  return Array.isArray(entry) ? entry : [entry];
};

type RelevantAttribute = { assignment_id: number; assignment: Assignment };

type NormalizedStatisticsInput = {
  mappingsByAssignment: Map<number, PostingTemplateMapping[]>;
  safeFieldNameSet: Set<string>;
  safeAllCommonality: Record<string, CommonalityConfigInput>;
  safeCommonalityByAssignment: CommonalityByAssignment;
  safeModifiedMappings: Set<string>;
  renderedByAssignment: RenderedByAssignment;
  renderErrorsByAssignment: Record<string | number, MaybeArray<unknown>> | null;
  hasRenderData: boolean;
};

const createStatisticsAccumulator = (hasRenderData: boolean): TemplateStatistics => ({
  ...createEmptyStatistics(),
  hasRenderData,
});

const finalizeStatistics = (stats: TemplateStatistics): TemplateStatistics => ({
  ...stats,
  mappedPercentage: stats.totalMappableAttributes > 0 ? Math.round((stats.mappedAttributes / stats.totalMappableAttributes) * 100) : 0,
  requiredMappedPercentage: stats.totalRequired > 0 ? Math.round((stats.mappedRequired / stats.totalRequired) * 100) : 0,
});

const addStatistics = (target: TemplateStatistics, source: TemplateStatistics): TemplateStatistics => {
  target.totalMappableAttributes += source.totalMappableAttributes;
  target.totalMappings += source.totalMappings;
  target.mappedAttributes += source.mappedAttributes;
  target.unmappedAttributes += source.unmappedAttributes;
  target.totalRequired += source.totalRequired;
  target.mappedRequired += source.mappedRequired;
  target.unmappedRequired += source.unmappedRequired;
  target.mappingErrors += source.mappingErrors;
  target.modifiedAttributes += source.modifiedAttributes;
  target.usesCommonality += source.usesCommonality;
  target.availableCommonality += source.availableCommonality;
  target.unmappedWithCommonality += source.unmappedWithCommonality;
  target.redundantMappings += source.redundantMappings;
  target.totalRendered += source.totalRendered;
  target.renderSuccess += source.renderSuccess;
  target.renderEmpty += source.renderEmpty;
  target.renderErrors += source.renderErrors;
  target.renderEmptyRequired += source.renderEmptyRequired;
  target.hasRenderData = target.hasRenderData || source.hasRenderData;
  return target;
};

const normalizeStatisticsInput = ({
  postingTemplateMappings = [],
  renderedByAssignment = null,
  renderErrorsByAssignment = null,
  fieldNames = [],
  allCommonality = {},
  commonalityByAssignment = {},
  modifiedMappings = new Set<string>(),
}: UseTemplateStatisticsProps): NormalizedStatisticsInput => {
  const safeMappings = Array.isArray(postingTemplateMappings) ? postingTemplateMappings : [];
  const mappingsByAssignment = new Map<number, PostingTemplateMapping[]>();
  safeMappings.forEach((mapping) => {
    const assignmentId = Number(mapping.assignment_id);
    const rows = mappingsByAssignment.get(assignmentId) ?? [];
    rows.push(mapping);
    mappingsByAssignment.set(assignmentId, rows);
  });

  const safeFieldNames = Array.isArray(fieldNames) ? fieldNames : [];
  const safeAllCommonality: Record<string, CommonalityConfigInput> = allCommonality && typeof allCommonality === 'object' ? allCommonality : {};
  const safeCommonalityByAssignment: CommonalityByAssignment =
    commonalityByAssignment && typeof commonalityByAssignment === 'object' ? commonalityByAssignment : {};
  const safeModifiedMappings =
    modifiedMappings && typeof (modifiedMappings as Set<unknown>).has === 'function' ? (modifiedMappings as Set<string>) : new Set<string>();

  return {
    mappingsByAssignment,
    safeFieldNameSet: new Set(safeFieldNames),
    safeAllCommonality,
    safeCommonalityByAssignment,
    safeModifiedMappings,
    renderedByAssignment,
    renderErrorsByAssignment,
    hasRenderData: Boolean(renderedByAssignment || renderErrorsByAssignment),
  };
};

const getAllMappableAttributes = (assignmentById: Record<string | number, Assignment>): RelevantAttribute[] =>
  Object.entries(assignmentById)
    .filter(([, assignment]) => (assignment as Assignment)?.is_mappable === 1)
    .map(([assignmentId, assignment]) => ({
      assignment_id: Number(assignmentId),
      assignment: assignment as Assignment,
    }));

const getCategoryOwnMappableAttributes = (
  category: CategoryFilterContainer | undefined,
  assignmentById: Record<string | number, Assignment>
): RelevantAttribute[] => {
  if (!category || !Array.isArray(category.attributeComponents)) {
    return [];
  }
  return category.attributeComponents.flatMap((attribute: CategoryFilterAttribute) => {
    const assignment = assignmentById?.[attribute.assignment_id] as Assignment | undefined;
    return assignment?.is_mappable === 1 ? [{ assignment_id: attribute.assignment_id, assignment }] : [];
  });
};

const getCategoryMappableAttributes = (
  category: CategoryFilterContainer | undefined,
  assignmentById: Record<string | number, Assignment>
): RelevantAttribute[] => {
  if (!category) {
    return [];
  }
  return [
    ...getCategoryOwnMappableAttributes(category, assignmentById),
    ...(category.containerComponents ?? []).flatMap((child) => getCategoryMappableAttributes(child, assignmentById)),
  ];
};

const computeStatisticsForAttributes = (relevantAttributes: RelevantAttribute[], normalized: NormalizedStatisticsInput): TemplateStatistics => {
  const stats = createStatisticsAccumulator(normalized.hasRenderData);

  relevantAttributes.forEach(({ assignment_id, assignment }) => {
    const mappings = normalized.mappingsByAssignment.get(assignment_id) ?? [];
    const hasValidMapping = mappings.some(isMappingValid);
    const isRequiredFlag = assignment.is_required === 1;
    const minOccursRequired = (assignment.min_occurs ?? 0) > 0 && assignment.ignore_validation !== 1;
    const isRequired = isRequiredFlag || minOccursRequired;

    stats.totalMappableAttributes++;
    stats.totalMappings += mappings.length;

    if (hasValidMapping) {
      stats.mappedAttributes++;
    } else {
      stats.unmappedAttributes++;
    }

    if (isRequired) {
      stats.totalRequired++;
      if (hasValidMapping) {
        stats.mappedRequired++;
      } else {
        stats.unmappedRequired++;
      }
    }

    const isModified = mappings.some((_, index) => normalized.safeModifiedMappings.has(`${assignment_id}-${index}`));
    if (isModified) {
      stats.modifiedAttributes++;
    }

    const hasMappingError = mappings.some((mapping) => {
      if (mapping?.value_type === 'product_property') {
        return Boolean(mapping.attribute_value && !normalized.safeFieldNameSet.has(mapping.attribute_value));
      }
      if (mapping?.value_type === 'commonality') {
        const commonalityConfig = getAssignmentCommonalityConfig(
          assignment,
          assignment_id,
          normalized.safeAllCommonality,
          normalized.safeCommonalityByAssignment
        );
        if (commonalityConfig?.default_value_type === 'product_property') {
          return Boolean(commonalityConfig.default_value && !normalized.safeFieldNameSet.has(commonalityConfig.default_value));
        }
      }
      return false;
    });
    if (hasMappingError) {
      stats.mappingErrors++;
    }

    const commonalityConfig = getAssignmentCommonalityConfig(
      assignment,
      assignment_id,
      normalized.safeAllCommonality,
      normalized.safeCommonalityByAssignment
    );

    if (commonalityConfig) {
      stats.availableCommonality++;
      const usesCommonality = mappings.some((m) => m.value_type === 'commonality');
      if (usesCommonality) {
        stats.usesCommonality++;
      }
      if (!hasValidMapping) {
        stats.unmappedWithCommonality++;
      }
      const redundantMapping = mappings.some(
        (mapping) =>
          mapping.value_type !== 'commonality' &&
          mapping.value_type === commonalityConfig.default_value_type &&
          mapping.attribute_value === commonalityConfig.default_value
      );
      if (redundantMapping) {
        stats.redundantMappings++;
      }
    }

    if (normalized.hasRenderData) {
      const renderedEntries = normalizeRenderedEntries(normalized.renderedByAssignment?.[assignment_id] as MaybeArray<RenderedEntry>);
      const renderErrors = normalizeRenderedEntries(normalized.renderErrorsByAssignment?.[assignment_id] as MaybeArray<RenderedEntry>);

      if (renderedEntries.length > 0 || renderErrors.length > 0) {
        stats.totalRendered++;

        const hasErrors = renderErrors.length > 0 || renderedEntries.some((entry) => Array.isArray(entry.errors) && entry.errors.length > 0);

        if (hasErrors) {
          stats.renderErrors++;
        } else {
          const hasNonEmptyValue = renderedEntries.some(
            (entry) => entry.channel_value !== null && entry.channel_value !== undefined && entry.channel_value !== ''
          );

          if (hasNonEmptyValue) {
            stats.renderSuccess++;
          } else {
            stats.renderEmpty++;
            if (isRequired) {
              stats.renderEmptyRequired++;
            }
          }
        }
      }
    }
  });

  return finalizeStatistics(stats);
};

export const computeTemplateStatistics = ({
  assignmentById = {},
  categoryFilter = null,
  ...rest
}: UseTemplateStatisticsProps): TemplateStatistics => {
  if (!assignmentById || typeof assignmentById !== 'object') {
    return createEmptyStatistics();
  }

  const normalized = normalizeStatisticsInput(rest);
  const relevantAttributes = categoryFilter
    ? getCategoryMappableAttributes(categoryFilter, assignmentById)
    : getAllMappableAttributes(assignmentById);
  return computeStatisticsForAttributes(relevantAttributes, normalized);
};

type CategoryStatsBaseArgs = Omit<UseTemplateStatisticsProps, 'categoryFilter'>;

// Computes all per-category statistics from one normalized set of lookups, caching
// each category node so nested category stats are only calculated once.
export const computeCategoryStatsByKey = (
  base: CategoryStatsBaseArgs,
  categoryByKey: Record<string, CategoryFilterContainer>
): Record<string, TemplateStatistics> => {
  const assignmentById = base.assignmentById;
  if (!assignmentById || typeof assignmentById !== 'object') {
    return {};
  }
  const normalized = normalizeStatisticsInput(base);
  const categoryCache = new WeakMap<object, TemplateStatistics>();

  const computeCategoryStats = (category: CategoryFilterContainer): TemplateStatistics => {
    if (!category) {
      return createStatisticsAccumulator(normalized.hasRenderData);
    }
    const cached = categoryCache.get(category);
    if (cached) {
      return cached;
    }

    const stats = computeStatisticsForAttributes(getCategoryOwnMappableAttributes(category, assignmentById), normalized);
    (category.containerComponents ?? []).forEach((child) => {
      addStatistics(stats, computeCategoryStats(child));
    });
    const finalized = finalizeStatistics(stats);
    categoryCache.set(category, finalized);
    return finalized;
  };

  const statsByKey: Record<string, TemplateStatistics> = {};
  for (const [key, category] of Object.entries(categoryByKey)) {
    statsByKey[key] = computeCategoryStats(category);
  }
  return statsByKey;
};

export const useTemplateStatistics = ({
  assignmentById,
  postingTemplateMappings,
  renderedByAssignment,
  renderErrorsByAssignment,
  fieldNames,
  allCommonality,
  commonalityByAssignment,
  modifiedMappings,
  categoryFilter,
}: UseTemplateStatisticsProps): TemplateStatistics =>
  useMemo(
    () =>
      computeTemplateStatistics({
        assignmentById,
        postingTemplateMappings,
        renderedByAssignment,
        renderErrorsByAssignment,
        fieldNames,
        allCommonality,
        commonalityByAssignment,
        modifiedMappings,
        categoryFilter,
      }),
    [
      assignmentById,
      postingTemplateMappings,
      renderedByAssignment,
      renderErrorsByAssignment,
      fieldNames,
      allCommonality,
      commonalityByAssignment,
      modifiedMappings,
      categoryFilter,
    ]
  );
